class Product2dVisualizer extends HTMLElement {
  constructor() {
    super();
    // Store references to elements and handlers
    this.stage = null;
    this.layer = null;
    this.observer = null;
    this.inputX = null; // Width input (_size_x)
    this.inputY = null; // Depth input (_size_y)
    
    // Store references to visual elements for updates
    this.frameGroup = null;
    this.infoGroup = null;
    this.containerId = null;
    
    // Configuration
    this.config = {
      stageWidth: 640,
      stageHeight: 300,
      padding: 60,
      labelOffset: 80,
      tickSize: 10,
      bgColor: '#5E5E5E',
      lineColor: '#D3D7DB',
      labelColor: '#89AA28',
      labelTitleColor: '#1D1D19',
      dimLineColor: 'black',
      fixedStripeSpacing: 15,
      fontSize: 16,
      fontFamily: 'Arial, sans-serif',
      preserveRatio: true
    };
    
    // Bind methods to this instance
    this.handleOptionChanged = this.handleOptionChanged.bind(this);
    this.updateVisualizer = this.updateVisualizer.bind(this);
  }

  connectedCallback() {
    // Find input fields first
    this.findInputElements();
    
    // Load Konva.js if it's not already loaded
    this.loadKonva().then(() => {
      console.log('Konva loaded, initializing visualizer...');
      // Sync initial values from inputs if available
      this.syncInputValues();
      this.initVisualizer();
      // Add listeners after initialization
      this.addInputListeners();
      // Listen for global option changes
      window.addEventListener('option:changed', this.handleOptionChanged);
    }).catch(error => {
      console.error('Failed to load Konva:', error);
      this.innerHTML = '<p style="color: red;">Error loading visualizer library.</p>';
    });
  }

  disconnectedCallback() {
    // Clean up Konva stage
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
    // Disconnect the observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    // Remove input listeners
    this.removeInputListeners();
    // Remove global event listeners
    window.removeEventListener('option:changed', this.handleOptionChanged);
    window.removeEventListener('resize', this.handleResize);
  }

  optionMap = {
    NO_FRAME: 1,
    FRAME: 0,
  }

  findInputElements() {
    // Find input elements within the dimension container
    const container = document.querySelector('.cl-po--dimension-container');
    if (container) {
      this.inputX = container.querySelector('input[data-dimension="width"]');
      this.inputY = container.querySelector('input[data-dimension="depth"]');
    } else {
      console.warn('Dimension container not found');
    }
    
    // Also look for the properties inputs
    if (!this.inputX) {
      this.inputX = document.querySelector('input[name="properties[_size_x]"]');
    }
    if (!this.inputY) {
      this.inputY = document.querySelector('input[name="properties[_size_y]"]');
    }
  }

  updateDimensionsFromInputs() {
    // Set the component's data attributes from the current input values
    // This ensures the visualizer starts with the correct values if they are pre-filled
    if (this.inputX && this.inputX.value) {
        this.dataset.widthValue = this.inputX.value;
    }
    if (this.inputY && this.inputY.value) {
        this.dataset.depthValue = this.inputY.value;
    }
  }

  loadKonva() {
    return new Promise((resolve, reject) => {
        if (window.Konva) {
            console.log('Konva already loaded.');
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/konva@9.3.6/konva.min.js'; // Using CDN
        script.async = true;
        script.onload = () => {
            console.log('Konva loaded successfully.');
            resolve();
        };
        script.onerror = (err) => {
            console.error('Error loading Konva script:', err);
            reject(err);
        }
        document.head.appendChild(script);
    });
  }

  initVisualizer() {
    console.log("Initializing Konva visualizer");
    if (!window.Konva) {
        console.error('Konva not loaded yet.');
        return;
    }

    // Prevent re-initialization if already done
    if (this.stage) {
        this.updateVisualizer();
        return;
    }

    // Update config with current values from dataset
    this.updateConfig();

    // Create a unique container ID
    this.containerId = 'konva-container-' + Math.random().toString(36).substring(2, 9);
    this.innerHTML = `<div id="${this.containerId}" class="konva-container" style="width:100%;"></div>`;
    const container = this.querySelector(`#${this.containerId}`);

    if (!container) {
        console.error('Visualizer: Konva container element not found.');
        return;
    }
    
    // Get the actual container width
    const containerWidth = container.offsetWidth || this.offsetWidth || 640;

    try {
        // Create stage and layer only once - use container width for stage width
        this.stage = new Konva.Stage({
            container: this.containerId,
            width: containerWidth, // Use actual container width
            height: this.config.stageHeight,
            offsetY: 40
        });
        
        // Add resize handler to adjust stage size when window resizes
        window.addEventListener('resize', () => {
            const newWidth = container.offsetWidth;
            if (newWidth > 0 && this.stage) {
                this.stage.width(newWidth);
                this.updateVisualizer();
            }
        });

        this.layer = new Konva.Layer();
        this.stage.add(this.layer);

        // Create main groups that will be updated
        const mainGroup = new Konva.Group();
        this.layer.add(mainGroup);

        // Now populate the visualizer with initial content
        this.updateVisualizer();

        // Set up observer for attribute changes
        this.setupObserver();

    } catch (error) {
        console.error("Error initializing Konva stage:", error);
        this.innerHTML = '<p style="color: red;">Error loading visualizer.</p>';
    }
  }

  updateConfig() {
    // Get the current container width if available
    let containerWidth = 640; // Default fallback
    if (this.containerId) {
      const container = document.getElementById(this.containerId);
      if (container) {
        containerWidth = container.offsetWidth;
      }
    }
    
    // Update configuration with current values
    this.config.stageWidth = containerWidth; // Use actual container width
    this.config.stageHeight = parseInt(this.dataset.height || this.getAttribute('data-height') || '400');
    this.config.mmWidthValue = parseFloat(this.dataset.widthValue || this.getAttribute('data-width-value') || '640');
    this.config.mmDepthValue = parseFloat(this.dataset.depthValue || this.getAttribute('data-depth-value') || '300');
    
    // Initialize frame setting if not already set
    if (this.dataset.frame === undefined) {
      this.dataset.frame = 'true'; // Default to FRAME
    }
    
    // Check container size and adjust scaling for smaller viewports
    // If container is less than 545px, make the rectangle twice as small
    if (containerWidth < 545) {
      this.config.smallScreenScaling = 1; // Scale to 50% (half size) for small screens
      // Adjust divider gap for smaller screens
      this.config.dividerGap = 12; // Smaller gap between dividers
    } else {
      this.config.smallScreenScaling = 1.0; // Normal scaling for larger screens
      this.config.dividerGap = 16; // Normal gap between dividers
    }
  }

  setupObserver() {
    // Clean up existing observer if any
    if (this.observer) {
        this.observer.disconnect();
    }

    // Create new observer for dimension changes
    this.observer = new MutationObserver(mutations => {
        let needsUpdate = false;
        mutations.forEach(mutation => {
            if (mutation.type === 'attributes' &&
                (mutation.attributeName === 'data-width-value' ||
                 mutation.attributeName === 'data-depth-value')) {
                needsUpdate = true;
            }
        });
        
        if (needsUpdate) {
            console.log('Dimensions changed, updating visualizer...');
            this.updateVisualizer();
        }
    });
    
    // Observe only the attributes relevant for updating dimensions
    this.observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-width-value', 'data-depth-value']
    });
  }

  updateVisualizer() {
    // Update configuration with current values
    this.updateConfig();
    
    if (!this.stage || !this.layer) {
        console.error('Cannot update visualizer: stage or layer not initialized');
        return;
    }

    // Clear existing content
    this.layer.destroyChildren();
    
    // Calculate dimensions and scaling
    const dimensions = this.calculateDimensions();
    
    // Create frame group (main rectangle and stripes)
    this.frameGroup = this.createFrame(dimensions);
    this.layer.add(this.frameGroup);
    
    // Create info group (dimension lines and labels)
    this.infoGroup = this.createInfo(dimensions);
    this.layer.add(this.infoGroup);

    // Draw everything
    this.layer.draw();
  }

  calculateDimensions() {
    // Prevent division by zero or negative values
    const safeMmWidth = Math.max(1, this.config.mmWidthValue);
    const safeMmDepth = Math.max(1, this.config.mmDepthValue);
    
    // Get small screen scaling factor (if set in updateConfig)
    const smallScreenScaling = this.config.smallScreenScaling || 1.0;
    
    // Reduced scaling ratio: 50mm = 200px (4px per mm) to make visualization smaller
    const scaleFactor = 3; // Reduced from 5 to 4 pixels per mm
    
    // Define custom padding values with increased space for labels
    // Adjust padding for smaller screens
    const paddingLeft = Math.floor((this.config.padding + 80) * smallScreenScaling); // Increased padding for left labels
    const paddingRight = 0
    const paddingTop = Math.floor(20 * smallScreenScaling); // Small padding at the top
    const paddingBottom = Math.floor((this.config.padding + 40) * smallScreenScaling); // Increased padding for bottom labels
    
    // Determine the maximum available space within the stage with adjusted padding
    const maxAvailableWidth = this.config.stageWidth - 160;
    const maxAvailableHeight = this.config.stageHeight - (paddingTop + paddingBottom);
    
    // Calculate dimensions based on fixed scale factor
    // Width and depth are scaled independently
    let scaledWidth = safeMmWidth * scaleFactor;
    let scaledHeight = safeMmDepth * scaleFactor;
    
    // Apply a scaling factor to make the visualization smaller
    // For small screens, use 0.5 (50%) of the original size, otherwise use 0.8 (80%)
    const additionalScaling = smallScreenScaling < 1.0 ? 0.5 : 0.8;
    scaledWidth *= additionalScaling;
    scaledHeight *= additionalScaling;
    
    // Check if the calculated dimensions exceed the available space
    // If they do, scale them down proportionally to maintain the correct ratio
    const widthRatio = maxAvailableWidth / scaledWidth;
    const heightRatio = maxAvailableHeight / scaledHeight;
    
    if (widthRatio < 1 || heightRatio < 1) {
        // Find the smallest ratio to ensure both dimensions fit
        const minRatio = Math.min(widthRatio, heightRatio);
        
        // Scale both dimensions by the same ratio to maintain the scaling ratio
        scaledWidth *= minRatio;
        scaledHeight *= minRatio;
    }
    
    // Calculate offsets to position the rectangle in the center of the stage (both horizontally and vertically)
    // Add 0.5 to ensure crisp rendering on pixel boundaries
    const offsetX = Math.round(this.config.stageWidth / 2 - scaledWidth / 2) + 20;
    const offsetY = Math.round(this.config.stageHeight / 2 - scaledHeight / 2) + 0.5;
    
    // Calculate the aspect ratio (only for reference, not used for scaling)
    const aspectRatio = safeMmWidth / safeMmDepth;

    return {
        width: safeMmWidth,
        height: safeMmDepth,
        scaledWidth,
        scaledHeight,
        offsetX,
        offsetY,
        aspectRatio
    };
  }

  createFrame(dimensions) {
    const { offsetX, offsetY, scaledWidth, scaledHeight } = dimensions;
    const group = new Konva.Group();
    
    // Check if frame is enabled or disabled
    const hasFrame = this.dataset.frame === 'true';
    
    // Get small screen scaling factor from config
    // This will be used for all size calculations in this method
    const smallScreenScaling = 1.0;
    
    // Define gap size for frame mode, adjust for small screens
    const gapSize = hasFrame ? Math.max(4, Math.floor(8 * smallScreenScaling)) : 0;
    
    // Calculate the total size (apply small screen scaling)
    const totalWidth = scaledWidth;
    const totalHeight = scaledHeight;
    
    // Calculate scaled frame dimensions and position
    const frameCenterX = offsetX + totalWidth / 2;
    const frameCenterY = offsetY + totalHeight / 2;
    const scaledFrameWidth = totalWidth * smallScreenScaling;
    const scaledFrameHeight = totalHeight * smallScreenScaling;
    const scaledFrameX = frameCenterX - (scaledFrameWidth / 2);
    const scaledFrameY = frameCenterY - (scaledFrameHeight / 2);
    
    // Adjust inner rectangle size when frame is enabled to keep total size constant
    // For the inner rectangle, we need to account for both the frame scaling and the gap
    const scaledGapSize = hasFrame ? Math.max(2, Math.floor(gapSize * smallScreenScaling)) : 0;
    const innerWidth = hasFrame ? scaledFrameWidth - (scaledGapSize * 2) : scaledFrameWidth;
    const innerHeight = hasFrame ? scaledFrameHeight - (scaledGapSize * 2) : scaledFrameHeight;
    
    // Adjust inner rectangle position when frame is enabled
    const innerX = hasFrame ? scaledFrameX + scaledGapSize : scaledFrameX;
    const innerY = hasFrame ? scaledFrameY + scaledGapSize : scaledFrameY;
    
    if (hasFrame) {
      // Outer frame with border (scaled to match the inner rectangle)
      const outerRect = new Konva.Rect({
        x: scaledFrameX,
        y: scaledFrameY,
        width: scaledFrameWidth,
        height: scaledFrameHeight,
        fill: '#D3D7DB', // White background for the gap
        stroke: '#000',
        strokeWidth: 1,
      });
      group.add(outerRect);
    }
    
    // Main rectangle (background) - just a simple gray background
    // We're using the inner dimensions directly since we've already applied scaling
    // to the frame and calculated the proper inner dimensions
    
    // Use the inner dimensions directly for the product rectangle
    const productRect = new Konva.Rect({
        x: innerX,
        y: innerY,
        width: innerWidth,
        height: innerHeight,
        fill: this.config.bgColor, // Gray background
        stroke: '#000',
        strokeWidth: 1,
    });
    group.add(productRect);
    
    // Add white horizontal dividers with dynamic gap based on screen size
    const dividerGap = this.config.dividerGap || 16; // Use configured divider gap or default to 16px
    const dividerColor = '#D3D7DB'; // White color for dividers
    const dividerHeight = 3; // 1px height for each divider
    
    // Calculate how many dividers we can fit based on the inner rectangle height
    const numDividers = Math.floor(innerHeight / dividerGap);
    
    // Add horizontal dividers
    for (let i = 1; i <= numDividers; i++) {
      // Calculate position ensuring even spacing within the inner rectangle
      const yPosition = innerY + (i * dividerGap);
      
      // Don't add divider if it would exceed the inner rectangle
      if (yPosition >= innerY + innerHeight) {
        break;
      }
      
      const divider = new Konva.Rect({
        x: innerX,
        y: yPosition,
        width: innerWidth,
        height: dividerHeight,
        fill: dividerColor,
        stroke: '#000',
        strokeWidth: 0.5,
      });
      
      group.add(divider);
    }
    return group;
  }
  
  createInfo(dimensions) {
    const { offsetX, offsetY, scaledWidth, scaledHeight, width, height } = dimensions;
    const group = new Konva.Group();
    
    // Create dimension lines and labels
    this.createWidthDimension(group, offsetX, offsetY, scaledWidth, scaledHeight, width);
    this.createDepthDimension(group, offsetX, offsetY, scaledWidth, scaledHeight, height);
    
    return group;
  }
  
  createWidthDimension(group, offsetX, offsetY, scaledWidth, scaledHeight, width) {
    // Width Dimension (Breite)
    const widthValueText = `${width}mm`;
    const widthTitleText = 'Breite';
    
    // Position dimension lines relative to the scaled rectangle
    // Add a fixed offset from the bottom of the rectangle
    const widthDimY = offsetY + scaledHeight + 30;
    
    // Create the dimension line
    const widthLine = new Konva.Line({
        points: [offsetX, widthDimY, offsetX + scaledWidth, widthDimY],
        stroke: this.config.dimLineColor,
        strokeWidth: 1,
    });
    group.add(widthLine);
    
    // Create tick marks
    const widthTickLeft = new Konva.Line({
        points: [offsetX, widthDimY - this.config.tickSize / 2, offsetX, widthDimY + this.config.tickSize / 2],
        stroke: this.config.dimLineColor,
        strokeWidth: 1,
    });
    group.add(widthTickLeft);
    
    const widthTickRight = new Konva.Line({
        points: [offsetX + scaledWidth, widthDimY - this.config.tickSize / 2, offsetX + scaledWidth, widthDimY + this.config.tickSize / 2],
        stroke: this.config.dimLineColor,
        strokeWidth: 1,
    });
    group.add(widthTickRight);
    
    // Create width value label
    const widthValueLabel = new Konva.Text({
        x: offsetX + scaledWidth / 2,
        y: offsetY + scaledHeight + 50, // Position below the dimension line
        text: widthValueText,
        fontSize: this.config.fontSize,
        fontFamily: this.config.fontFamily,
        fill: this.config.labelColor,
        fontStyle: 'bold',
        align: 'center',
        lineHeight: 1.2,
        padding: 5,
    });
    
    // Center the label
    widthValueLabel.offsetX(widthValueLabel.width() / 2);
    widthValueLabel.offsetY(widthValueLabel.height() / 2);
    
    // Create width title label with improved positioning
    const widthTitleLabel = new Konva.Text({
        x: offsetX + scaledWidth / 2,
        y: offsetY + scaledHeight + 75, // Position below the value label
        text: widthTitleText,
        fontSize: this.config.fontSize,
        fontFamily: this.config.fontFamily,
        fill: this.config.labelTitleColor,
        align: 'center',
        lineHeight: 1.2,
        padding: 5,
    });
    
    // Center the label
    widthTitleLabel.offsetX(widthTitleLabel.width() / 2);
    widthTitleLabel.offsetY(widthTitleLabel.height() / 2);
    
    // Create background rectangles for labels
    const widthValueLabelBg = new Konva.Rect({
        x: widthValueLabel.x() - widthValueLabel.width() / 2,
        y: widthValueLabel.y() - widthValueLabel.height() / 2,
        width: widthValueLabel.width(),
        height: widthValueLabel.height(),
        fill: '#F8F8F8', // Match main background color
    });
    
    const widthTitleLabelBg = new Konva.Rect({
        x: widthTitleLabel.x() - widthTitleLabel.width() / 2,
        y: widthTitleLabel.y() - (widthTitleLabel.height() / 2 - 40),
        width: widthTitleLabel.width(),
        height: widthTitleLabel.height(),
        fill: '#F8F8F8', // Match main background color
    });
    
    // Add background rectangles first (so they're behind the text)
    group.add(widthValueLabelBg);
    group.add(widthTitleLabelBg);
    
    // Add labels on top
    group.add(widthValueLabel);
    group.add(widthTitleLabel);
  }
  
  createDepthDimension(group, offsetX, offsetY, scaledWidth, scaledHeight, height) {
    // Depth Dimension (Länge)
    const depthLabelText = `${height}mm`;
    const depthTitleText = 'Länge';
    
    // Position dimension lines relative to the scaled rectangle
    // Add a fixed offset from the left of the rectangle
    const depthDimX = offsetX -50;
    
    // Create the dimension line
    const depthLine = new Konva.Line({
        points: [depthDimX, offsetY, depthDimX, offsetY + scaledHeight],
        stroke: this.config.dimLineColor,
        strokeWidth: 1,
    });
    group.add(depthLine);
    
    // Create tick marks
    const depthTickTop = new Konva.Line({
        points: [depthDimX - this.config.tickSize / 2, offsetY, depthDimX + this.config.tickSize / 2, offsetY],
        stroke: this.config.dimLineColor,
        strokeWidth: 1,
    });
    group.add(depthTickTop);
    
    const depthTickBottom = new Konva.Line({
        points: [depthDimX - this.config.tickSize / 2, offsetY + scaledHeight, depthDimX + this.config.tickSize / 2, offsetY + scaledHeight],
        stroke: this.config.dimLineColor,
        strokeWidth: 1,
    });
    group.add(depthTickBottom);
    
    // Create depth value label with adjusted position for better readability
    const depthLabel = new Konva.Text({
        x: offsetX - 50, // Position to the left of the dimension line
        y: offsetY + scaledHeight / 2, // Center vertically
        text: depthLabelText,
        fontSize: this.config.fontSize,
        fontFamily: this.config.fontFamily,
        fill: this.config.labelColor,
        fontStyle: 'bold',
        align: 'center',
        rotation: 0,
        lineHeight: 1.2,
        padding: 5,
    });
    
    // Center the label
    depthLabel.offsetX(depthLabel.width() / 2);
    depthLabel.offsetY(depthLabel.height() / 2 +10);
    
    // Create depth title label with adjusted position for better readability
    const depthTitleLabel = new Konva.Text({
        x: offsetX - this.config.labelOffset +35, // Position further left with the increased padding
        y: offsetY + (scaledHeight / 2), // Position at 2/3 of the height for better spacing
        text: depthTitleText,
        fontSize: this.config.fontSize,
        fontFamily: this.config.fontFamily,
        fill: this.config.labelTitleColor,
        align: 'center',
        rotation: 0,
        lineHeight: 1.2,
        padding: 5,
    });
    
    // Center the label
    depthTitleLabel.offsetX(depthTitleLabel.width() / 2);
    depthTitleLabel.offsetY((depthTitleLabel.height() / 2) -15);
    
    // Create background rectangles for labels
    const depthLabelBg = new Konva.Rect({
        x: depthLabel.x(),
        y: depthLabel.y(),
        width: depthLabel.width(),
        height: depthLabel.height(),
        fill: '#F8F8F8', // Match main background color
        offsetX: depthLabel.offsetX(),
        offsetY: depthLabel.offsetY(),
        rotation: depthLabel.rotation(),
    });
    
    const depthTitleLabelBg = new Konva.Rect({
        x: depthTitleLabel.x(),
        y: depthTitleLabel.y(),
        width: depthTitleLabel.width(),
        height: depthTitleLabel.height(),
        fill: '#F8F8F8', // Match main background color
        offsetX: depthTitleLabel.offsetX(),
        offsetY: depthTitleLabel.offsetY(),
    });
    
    // Add background rectangles first
    group.add(depthLabelBg);
    group.add(depthTitleLabelBg);
    
    // Center the group of depth labels around the midpoint of the dimension line
    const depthMidY = offsetY + scaledHeight / 2;
    const depthLabelGap = 5; // Gap between title and value
    
    // Position Title Label above midpoint
    depthTitleLabel.y(depthMidY - (depthLabelGap / 2));
    // Position Value Label below midpoint
    depthLabel.y(depthMidY + (depthLabelGap / 2));
    
    // Add labels on top
    group.add(depthLabel);
    group.add(depthTitleLabel);
  }

  // Find input fields for width and depth
  findInputFields() {
    this.inputX = document.querySelector('input[name="properties[_size_x]"]');
    this.inputY = document.querySelector('input[name="properties[_size_y]"]');
    
    if (!this.inputX) {
      console.warn('Width input (properties[_size_x]) not found');
    }
    if (!this.inputY) {
      console.warn('Depth input (properties[_size_y]) not found');
    }
  }

  // Add global option:changed event listener
  addInputListeners() {
    // We're already adding the option:changed listener in connectedCallback
    // This method is kept for API compatibility
  }

  // Remove event listeners
  removeInputListeners() {
    // We're already removing the option:changed listener in disconnectedCallback
    // This method is kept for API compatibility
  }

  // Handle option:changed event
  handleOptionChanged(event) {
    // Get the option from event detail
    const option = event.detail;
    
    let dimensionsChanged = false;
    const livePriceEl = document.querySelector('.live-price');

    if(option && option.name =="type"){
      this.classList.add("visible");
      if(livePriceEl){
        livePriceEl.classList.add("visible");
      }
    }

    

    // Check if this has the _controller object with x and y values
    if (option && option._controller) {
      const controller = option._controller;
      
      // Update width if x value exists and is valid
      if (controller.x && !isNaN(parseFloat(controller.x))) {
        const xValue = parseFloat(controller.x);
        if (this.dataset.widthValue !== String(xValue)) {
          this.dataset.widthValue = xValue;
          dimensionsChanged = true;
        }
      }
      
      // Update depth if y value exists and is valid
      if (controller.y && !isNaN(parseFloat(controller.y))) {
        const yValue = parseFloat(controller.y);
        if (this.dataset.depthValue !== String(yValue)) {
          this.dataset.depthValue = yValue;
          dimensionsChanged = true;
        }
      }
      
      // If dimensions changed, update the visualizer directly
      if (dimensionsChanged && this.stage) {
        this.updateVisualizer();
      }
    }
    if(option && option.name == "type"){
      if(option.value == this.optionMap.NO_FRAME){
        this.dataset.frame = false;
      }else if(option.value == this.optionMap.FRAME){
        this.dataset.frame = true;
      }
      
      // Update the visualizer to reflect the frame change
      if (this.stage) {
        this.updateVisualizer();
      }
    }

    if(option && option.name == "color"){
      const colorValue = option.value;
      const colorHEX = option.values.find(value => value.value == colorValue);
      if(colorHEX){
        this.config.bgColor = colorHEX.color
        if (this.stage) {
          this.updateVisualizer();
        }
      }
    }
  }

  // Sync initial values from inputs
  syncInputValues() {
    if (this.inputX && this.inputX.value && !isNaN(parseFloat(this.inputX.value))) {
      this.dataset.widthValue = this.inputX.value;
    }
    if (this.inputY && this.inputY.value && !isNaN(parseFloat(this.inputY.value))) {
      this.dataset.depthValue = this.inputY.value;
    }
  }
}

// Define the custom element if it hasn't been defined already
if (!customElements.get('product-2d-visualizer')) {
    customElements.define('product-2d-visualizer', Product2dVisualizer);
}
